import { createFileRoute } from "@tanstack/react-router";
import { supabase } from "@/integrations/supabase/client";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { format } from "date-fns";
import {
  AlertTriangle,
  Download,
  RefreshCw,
  Eye,
  Copy,
  Wand2,
  Bug,
  CheckCircle2,
  RotateCcw,
  StickyNote,
} from "lucide-react";
import { isCriticalCategory } from "@/lib/error-logger";
import { isDebugLoggingEnabled, setDebugLogging } from "@/lib/debug-logging";
import { maskObject } from "@/lib/breadcrumbs";

export const Route = createFileRoute("/admin/errors")({
  head: () => ({ meta: [{ title: "App Errors — Admin" }] }),
  component: AdminErrorsPage,
});

type Row = {
  id: string;
  created_at: string;
  user_id: string | null;
  user_phone: string | null;
  page: string | null;
  message: string;
  stack: string | null;
  severity: string;
  category: string;
  source: string;
  screenshot_path: string | null;
  meta: any;
  status?: string | null;
  admin_note?: string | null;
  group_hash?: string | null;
  resolved_at?: string | null;
};

type Group = {
  key: string;
  latest: Row;
  count: number;
  firstSeen: string;
  lastSeen: string;
  users: Set<string>;
  rows: Row[];
};

const SEVERITY_STYLES: Record<string, string> = {
  critical: "bg-red-100 text-red-800 border-red-300",
  error: "bg-orange-100 text-orange-800 border-orange-300",
  warning: "bg-amber-100 text-amber-900 border-amber-300",
  info: "bg-blue-100 text-blue-800 border-blue-300",
};

const STATUS_STYLES: Record<string, string> = {
  new: "bg-slate-100 text-slate-800 border-slate-300",
  investigating: "bg-blue-100 text-blue-800 border-blue-300",
  fix_prompt_copied: "bg-violet-100 text-violet-800 border-violet-300",
  fixed: "bg-emerald-100 text-emerald-800 border-emerald-300",
  ignored: "bg-zinc-100 text-zinc-700 border-zinc-300",
};

const STATUS_LABEL: Record<string, string> = {
  new: "New",
  investigating: "Investigating",
  fix_prompt_copied: "Fix Prompt Copied",
  fixed: "Fixed",
  ignored: "Ignored",
};

const NA = "Not captured";

function nn(v: any): string {
  if (v === null || v === undefined || v === "") return NA;
  return String(v);
}

// Explain WHY a field is missing rather than a bare "Not captured".
// meta.source_maps_reason is set by the client symbolicator; script_error_explanation
// is set when the browser masked the failure to a generic "Script error".
function whyMissing(meta: any): string {
  if (meta?.script_error_explanation) {
    return "Not captured — browser masked this as a generic 'Script error' (cross-origin script / missing crossorigin attribute). See explanation below.";
  }
  const r = meta?.source_maps_reason;
  if (r === "source_map_missing") return "Not captured — source map (.js.map) was not found for this production build.";
  if (r === "source_map_cors_blocked") return "Not captured — browser blocked source map fetch due to CORS.";
  if (r === "symbolicate_timeout") return "Not captured — source map fetch timed out (>4s) on the customer's device.";
  if (r === "symbolicate_failed") return "Not captured — source map found but could not be applied to this stack.";
  if (r === "no_frames_resolved") return "Not captured — stack trace could not be parsed into frames.";
  if (r === "no_stack") return "Not captured — the browser did not attach a stack trace to this error.";
  if (meta?.source_maps_available === false) return "Not captured — source maps unavailable for this build.";
  return NA;
}

function nnWhy(v: any, meta: any): string {
  if (v === null || v === undefined || v === "") return whyMissing(meta);
  return String(v);
}

function fmtViewport(v: any): string {
  if (!v) return NA;
  return `${v.width}x${v.height} @${v.dpr}dpr`;
}

function fmtDevice(meta: any): string {
  const parts = [
    meta?.device_model,
    meta?.os && meta?.os_version ? `${meta.os} ${meta.os_version}` : meta?.os,
  ].filter(Boolean);
  return parts.length ? parts.join(" • ") : NA;
}

function fmtBrowser(meta: any): string {
  if (!meta?.browser) return meta?.user_agent ?? NA;
  const v = meta.browser_version ? ` ${meta.browser_version}` : "";
  const wv = meta.is_webview ? " (WebView)" : "";
  return `${meta.browser}${v}${wv}`;
}

function isScriptError(r: Row): boolean {
  const m = (r.message ?? "").toLowerCase();
  return m === "script error." || m === "script error" || (!r.stack && !r.meta?.filename);
}

function firstStackFrame(stack: string | null | undefined): string {
  if (!stack) return NA;
  return stack.split("\n").slice(0, 3).join("\n");
}

function extractFetchFailures(meta: any): Array<any> {
  const bc: any[] = Array.isArray(meta?.breadcrumbs) ? meta.breadcrumbs : [];
  return bc.filter((b) => b?.kind === "fetch");
}

function extractConsoleLogs(meta: any): Array<any> {
  const bc: any[] = Array.isArray(meta?.breadcrumbs) ? meta.breadcrumbs : [];
  return bc.filter((b) => b?.kind === "console");
}

function extractRecentActions(meta: any): Array<any> {
  const bc: any[] = Array.isArray(meta?.breadcrumbs) ? meta.breadcrumbs : [];
  return bc.filter((b) => b?.kind === "click" || b?.kind === "nav" || b?.kind === "action");
}

function lastUserAction(meta: any): string {
  const acts = extractRecentActions(meta);
  const last = acts[acts.length - 1];
  return last ? `${last.kind}: ${last.message}` : NA;
}

function fmtBreadcrumbLine(b: any): string {
  const t = b?.t ? new Date(b.t).toISOString().slice(11, 19) : "??:??:??";
  return `[${t}] ${b?.kind ?? "?"} ${b?.message ?? ""}`;
}

function suggestedCause(r: Row): string {
  if (isScriptError(r)) {
    return "Cross-origin script (masked as 'Script error') or a third-party asset threw. Check the last user action and failed fetch calls below for the true origin.";
  }
  const m = (r.message ?? "").toLowerCase();
  if (m.includes("network") || m.includes("failed to fetch")) return "Network / fetch failure. Check the Failed API Requests section for the exact endpoint.";
  if (m.includes("permission") || m.includes("row-level security") || m.includes("rls")) return "Supabase RLS policy denied the operation. Verify policy on the target table and that user is signed in.";
  if (m.includes("undefined") || m.includes("null")) return "Reading a property from an undefined/null value. Add a guard for the missing data or fix the loader that should have populated it.";
  if (m.includes("hydration")) return "SSR/CSR hydration mismatch. A component rendered different HTML on server vs client (e.g. Date.now, localStorage read in initial state).";
  return "Root cause not obvious from message alone — see stack trace + recent user action + failed API calls.";
}

function buildFullReport(r: Row): string {
  const meta = r.meta ?? {};
  const fetches = extractFetchFailures(meta);
  const cons = extractConsoleLogs(meta);
  const actions = extractRecentActions(meta);
  const lines: string[] = [];
  const P = (label: string, val: any) => lines.push(`${label}: ${nn(val)}`);

  lines.push("=== ERROR SUMMARY ===");
  P("Title", meta.name ?? "Error");
  P("Message", r.message);
  P("Type", meta.name);
  P("Category", r.category);
  P("Severity", r.severity);
  P("Status", STATUS_LABEL[r.status ?? "new"] ?? r.status);
  P("Time", format(new Date(r.created_at), "dd MMM yyyy, HH:mm:ss"));
  P("Captured At", meta.captured_at);
  lines.push("");

  lines.push("=== WHERE IT HAPPENED ===");
  P("Page/Route", r.page);
  P("Full URL", meta.url);
  lines.push(`Source File: ${nnWhy(meta.filename, meta)}`);
  lines.push(`Line: ${nnWhy(meta.lineno, meta)}`);
  lines.push(`Column: ${nnWhy(meta.colno, meta)}`);
  P("Function/Component", meta.function_name ?? (meta.componentStack ? String(meta.componentStack).split("\n")[1]?.trim() : meta.boundary));
  P("Source Maps", meta.source_maps_available === true ? `available (${meta.source_maps_reason ?? "ok"})` : `unavailable (${meta.source_maps_reason ?? "unknown"})`);
  if (meta.raw_filename && meta.raw_filename !== meta.filename) {
    P("Minified Source File", meta.raw_filename);
    P("Minified Line", meta.raw_lineno);
    P("Minified Column", meta.raw_colno);
  }
  if (meta.script_error_explanation) {
    lines.push("");
    lines.push("Why file/line/column are missing:");
    lines.push(String(meta.script_error_explanation));
  }
  lines.push("");

  lines.push("=== DEVICE & APP INFORMATION ===");
  P("App Version", meta.app_version);
  P("Device Model", meta.device_model);
  P("OS", meta.os);
  P("OS Version", meta.os_version);
  P("Android Version", meta.android_version);
  P("Browser/WebView", fmtBrowser(meta));
  P("Platform", meta.platform);
  P("Language", meta.language);
  P("Viewport", fmtViewport(meta.viewport));
  P("Network Online", meta.network_online);
  P("Connection", meta.connection ? JSON.stringify(meta.connection) : NA);
  P("User Agent", meta.user_agent);
  P("User ID", r.user_id);
  P("User Mobile", r.user_phone);
  lines.push("");

  lines.push("=== STACK TRACE ===");
  lines.push(r.stack ?? NA);
  lines.push("");

  lines.push("=== CONSOLE LOGS (recent) ===");
  if (cons.length === 0) lines.push(NA);
  else for (const b of cons) lines.push(fmtBreadcrumbLine(b));
  lines.push("");

  lines.push("=== NETWORK / API FAILURES ===");
  if (fetches.length === 0) lines.push(NA);
  else for (const b of fetches) {
    const d = b.data ?? {};
    lines.push(`- ${d.method ?? "?"} ${d.url ?? b.message} → ${d.status ?? "network error"}${d.ms ? ` (${d.ms}ms)` : ""}`);
  }
  lines.push("");

  lines.push("=== BACKEND / SUPABASE DETAILS ===");
  P("Category is backend", r.category === "backend" ? "yes" : "no");
  P("Source", r.source);
  const supaBc = (Array.isArray(meta.breadcrumbs) ? meta.breadcrumbs : []).filter((b: any) => b.kind === "supabase");
  if (supaBc.length) supaBc.forEach((b: any) => lines.push(fmtBreadcrumbLine(b)));
  else lines.push("(no Supabase-tagged breadcrumbs)");
  lines.push("");

  lines.push("=== RECENT USER ACTIONS ===");
  if (actions.length === 0) lines.push(NA);
  else for (const b of actions) lines.push(fmtBreadcrumbLine(b));
  P("Last action before error", lastUserAction(meta));
  lines.push("");

  lines.push("=== SUGGESTED CAUSE ===");
  lines.push(suggestedCause(r));
  return lines.join("\n");
}

function buildLovableFixPrompt(r: Row): string {
  const meta = r.meta ?? {};
  const fetches = extractFetchFailures(meta);
  const cons = extractConsoleLogs(meta);
  const fetchBlock = fetches.length
    ? fetches
        .map((b) => {
          const d = b.data ?? {};
          return `- ${d.method ?? "?"} ${d.url ?? b.message} → status ${d.status ?? "network error"}`;
        })
        .join("\n")
    : NA;
  const consBlock = cons.length ? cons.map((b) => fmtBreadcrumbLine(b)).join("\n") : NA;

  return [
    `Inspect and fix this exact error in the existing Brothers Club app.`,
    ``,
    `Error:`,
    r.message || NA,
    ``,
    `Error Type: ${nn(meta.name)}`,
    `Category: ${nn(r.category)}`,
    `Severity: ${nn(r.severity)}`,
    `Time: ${format(new Date(r.created_at), "dd MMM yyyy, HH:mm:ss")}`,
    ``,
    `Page/Route:`,
    `${nn(r.page)}  (full URL: ${nn(meta.url)})`,
    ``,
    `File:`,
    nnWhy(meta.filename, meta),
    ``,
    `Line/Column:`,
    `${nnWhy(meta.lineno, meta)}:${nnWhy(meta.colno, meta)}`,
    ``,
    `Function/Component:`,
    nn(meta.function_name),
    ``,
    `Source Maps: ${meta.source_maps_available === true ? "available" : `unavailable (${meta.source_maps_reason ?? "unknown"})`}`,
    meta.script_error_explanation ? `Why file/line/column missing: ${meta.script_error_explanation}` : ``,
    ``,
    `Stack Trace (symbolicated when source maps available; original minified below):`,
    r.stack ?? NA,
    ``,
    `Console Logs:`,
    consBlock,
    ``,
    `Failed API Request:`,
    fetchBlock,
    ``,
    `Device/App Context:`,
    `Device: ${fmtDevice(meta)}`,
    `Browser/WebView: ${fmtBrowser(meta)}`,
    `App Version: ${nn(meta.app_version)}`,
    `Android Version: ${nn(meta.android_version)}`,
    `Viewport: ${fmtViewport(meta.viewport)}`,
    `Network Online: ${nn(meta.network_online)}`,
    ``,
    `Recent User Action:`,
    lastUserAction(meta),
    ``,
    `Required work:`,
    `1. Find the exact root cause.`,
    `2. Fix only the affected code.`,
    `3. Do not change unrelated app features.`,
    `4. Do not remove existing functionality.`,
    `5. Preserve current database records and wallet/payment behaviour.`,
    `6. Add error handling so this issue does not crash the page again.`,
    `7. Run typecheck/build tests.`,
    `8. Explain what was broken, which files were changed and how the fix was verified.`,
  ].join("\n");
}

async function copyToClipboard(text: string, label: string) {
  try {
    await navigator.clipboard.writeText(text);
    toast.success(`${label} — Copied successfully`);
  } catch {
    toast.error("Copy failed");
  }
}

function AdminErrorsPage() {
  const qc = useQueryClient();
  const [from, setFrom] = useState<string>("");
  const [to, setTo] = useState<string>("");
  const [phone, setPhone] = useState("");
  const [page, setPage] = useState("");
  const [severity, setSeverity] = useState<string>("all");
  const [category, setCategory] = useState<string>("all");
  const [status, setStatus] = useState<string>("all");
  const [grouped, setGrouped] = useState<boolean>(true);
  const [detailsRow, setDetailsRow] = useState<Row | null>(null);
  const [debugOn, setDebugOn] = useState<boolean>(() => isDebugLoggingEnabled());

  const { data, isLoading, refetch, isFetching } = useQuery({
    queryKey: ["admin_app_errors", from, to, phone, page, severity, category, status],
    queryFn: async () => {
      let q = supabase.from("app_errors").select("*").order("created_at", { ascending: false }).limit(500);
      if (from) q = q.gte("created_at", new Date(from).toISOString());
      if (to) { const d = new Date(to); d.setDate(d.getDate() + 1); q = q.lt("created_at", d.toISOString()); }
      if (phone.trim()) q = q.ilike("user_phone", `%${phone.trim()}%`);
      if (page.trim()) q = q.ilike("page", `%${page.trim()}%`);
      if (severity !== "all") q = q.eq("severity", severity);
      if (category !== "all") q = q.eq("category", category);
      if (status !== "all") q = q.eq("status", status);
      const { data, error } = await q;
      if (error) throw error;
      return (data ?? []) as Row[];
    },
  });

  const rows = data ?? [];

  const groups: Group[] = useMemo(() => {
    const map = new Map<string, Group>();
    for (const r of rows) {
      const key = r.group_hash || `${r.message}|${r.page ?? ""}`;
      let g = map.get(key);
      if (!g) {
        g = { key, latest: r, count: 0, firstSeen: r.created_at, lastSeen: r.created_at, users: new Set(), rows: [] };
        map.set(key, g);
      }
      g.count += 1;
      g.rows.push(r);
      if (r.user_id) g.users.add(r.user_id);
      if (new Date(r.created_at) > new Date(g.lastSeen)) { g.lastSeen = r.created_at; g.latest = r; }
      if (new Date(r.created_at) < new Date(g.firstSeen)) g.firstSeen = r.created_at;
    }
    return Array.from(map.values()).sort((a, b) => +new Date(b.lastSeen) - +new Date(a.lastSeen));
  }, [rows]);

  const summary = useMemo(() => {
    const total = rows.length;
    const critical = rows.filter((r) => r.severity === "critical" || isCriticalCategory(r.category)).length;
    const open = rows.filter((r) => (r.status ?? "new") !== "fixed" && (r.status ?? "new") !== "ignored").length;
    return { total, critical, open, groups: groups.length };
  }, [rows, groups]);

  const updateStatus = useMutation({
    mutationFn: async (p: { ids: string[]; status: string; note?: string | null }) => {
      const patch: any = { status: p.status };
      if (p.status === "fixed") patch.resolved_at = new Date().toISOString();
      if (p.status !== "fixed") patch.resolved_at = null;
      if (p.note !== undefined) patch.admin_note = p.note;
      const { error } = await supabase.from("app_errors").update(patch).in("id", p.ids);
      if (error) throw error;
    },
    onSuccess: () => { toast.success("Updated"); qc.invalidateQueries({ queryKey: ["admin_app_errors"] }); },
    onError: (e: any) => toast.error(e?.message ?? "Update failed"),
  });

  function enableDebugAndReproduce(r: Row | null) {
    setDebugLogging(true);
    setDebugOn(true);
    toast.success("Debug logging active. Reproduce the issue now.");
    if (r?.page) { try { window.open(r.page, "_blank"); } catch {} }
  }

  function exportCSV() {
    if (!rows.length) return;
    const headers = ["created_at", "user_phone", "page", "severity", "category", "status", "source", "message"];
    const esc = (v: any) => { const s = v == null ? "" : String(v); return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s; };
    const csv = [headers.join(","), ...rows.map((r) => headers.map((h) => esc((r as any)[h])).join(","))].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = `app-errors-${Date.now()}.csv`; a.click();
    URL.revokeObjectURL(url);
  }

  return (
    <div className="space-y-4">
      {debugOn && (
        <div className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-amber-400 bg-amber-50 px-3 py-2 text-xs text-amber-900">
          <div className="font-semibold">🐞 Debug logging is active. Reproduce the issue now — the next error will be saved with full details.</div>
          <Button size="sm" variant="outline" className="h-7" onClick={() => { setDebugLogging(false); setDebugOn(false); toast.success("Debug logging stopped."); }}>
            Stop Debug Logging
          </Button>
        </div>
      )}

      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h1 className="text-lg font-bold">App Errors</h1>
          <p className="text-xs text-muted-foreground">
            {summary.total} entries • {summary.groups} unique • <span className="font-semibold text-red-700">{summary.critical} critical</span> • {summary.open} open
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant={grouped ? "default" : "outline"} size="sm" onClick={() => setGrouped((g) => !g)}>
            {grouped ? "Grouped" : "Flat"}
          </Button>
          <Button variant={debugOn ? "default" : "outline"} size="sm" onClick={() => enableDebugAndReproduce(null)}>
            <Bug className="h-4 w-4 mr-1" /> Enable Debug & Reproduce
          </Button>
          <Button variant="outline" size="sm" onClick={() => refetch()} disabled={isFetching}>
            <RefreshCw className={`h-4 w-4 ${isFetching ? "animate-spin" : ""}`} />
          </Button>
          <Button variant="outline" size="sm" onClick={exportCSV}><Download className="h-4 w-4 mr-1" />CSV</Button>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-2 rounded-xl bg-card p-3 shadow-card sm:grid-cols-3 lg:grid-cols-7">
        <div><Label className="text-[11px]">From</Label><Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} /></div>
        <div><Label className="text-[11px]">To</Label><Input type="date" value={to} onChange={(e) => setTo(e.target.value)} /></div>
        <div><Label className="text-[11px]">Mobile</Label><Input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+91…" /></div>
        <div><Label className="text-[11px]">Page</Label><Input value={page} onChange={(e) => setPage(e.target.value)} placeholder="/wallet" /></div>
        <div>
          <Label className="text-[11px]">Severity</Label>
          <Select value={severity} onValueChange={setSeverity}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="critical">Critical</SelectItem>
              <SelectItem value="error">Error</SelectItem>
              <SelectItem value="warning">Warning</SelectItem>
              <SelectItem value="info">Info</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-[11px]">Category</Label>
          <Select value={category} onValueChange={setCategory}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="frontend">Frontend</SelectItem>
              <SelectItem value="backend">Backend</SelectItem>
              <SelectItem value="otp">OTP</SelectItem>
              <SelectItem value="deposit">Deposit</SelectItem>
              <SelectItem value="bet">Bet</SelectItem>
              <SelectItem value="wallet">Wallet</SelectItem>
              <SelectItem value="result">Result</SelectItem>
              <SelectItem value="report">User Report</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-[11px]">Status</Label>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="new">New</SelectItem>
              <SelectItem value="investigating">Investigating</SelectItem>
              <SelectItem value="fix_prompt_copied">Fix Prompt Copied</SelectItem>
              <SelectItem value="fixed">Fixed</SelectItem>
              <SelectItem value="ignored">Ignored</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="overflow-hidden rounded-xl bg-card shadow-card">
        {isLoading ? (
          <div className="p-6 text-center text-sm text-muted-foreground">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="p-6 text-center text-sm text-muted-foreground">No errors match these filters.</div>
        ) : (
          <ul className="divide-y">
            {(grouped ? groups.map((g) => g.latest) : rows).map((r) => {
              const g = grouped ? groups.find((x) => x.latest.id === r.id) : null;
              const critical = r.severity === "critical" || isCriticalCategory(r.category);
              const sevClass = SEVERITY_STYLES[r.severity] ?? SEVERITY_STYLES.error;
              const st = r.status ?? "new";
              const stClass = STATUS_STYLES[st] ?? STATUS_STYLES.new;
              const meta = r.meta ?? {};
              return (
                <li key={r.id} className={critical ? "bg-red-50/60" : ""}>
                  <div className="flex items-start gap-3 p-3">
                    {critical && <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-red-600" />}
                    <div className="min-w-0 flex-1 space-y-1.5">
                      <div className="flex flex-wrap items-center gap-1.5 text-[11px]">
                        <span className={`rounded border px-1.5 py-0.5 font-semibold uppercase ${sevClass}`}>{r.severity}</span>
                        <span className="rounded border border-border bg-muted px-1.5 py-0.5 font-semibold uppercase">{r.category}</span>
                        <span className={`rounded border px-1.5 py-0.5 font-semibold uppercase ${stClass}`}>{STATUS_LABEL[st] ?? st}</span>
                        {meta.name && (
                          <span className="rounded border border-border bg-muted px-1.5 py-0.5 font-mono font-semibold">
                            {meta.name}
                          </span>
                        )}
                        {g && g.count > 1 && (
                          <span className="rounded border border-red-300 bg-red-50 px-1.5 py-0.5 font-semibold text-red-700">
                            ×{g.count} • {g.users.size} user{g.users.size === 1 ? "" : "s"}
                          </span>
                        )}
                        <span className="text-muted-foreground">
                          {format(new Date(r.created_at), "dd MMM yyyy, HH:mm:ss")}
                        </span>
                      </div>

                      <p className="break-words text-sm font-semibold">{r.message}</p>

                      <div className="grid grid-cols-1 gap-x-3 gap-y-0.5 text-[11px] text-muted-foreground sm:grid-cols-2">
                        <div className="truncate"><span className="font-semibold">Page:</span> {nn(r.page)}</div>
                        <div className="truncate"><span className="font-semibold">File:</span> {nnWhy(meta.filename, meta)}</div>
                        <div><span className="font-semibold">Line:Col:</span> {nnWhy(meta.lineno, meta)}:{nnWhy(meta.colno, meta)}</div>
                        <div><span className="font-semibold">User:</span> {r.user_phone ?? r.user_id ?? "anonymous"}</div>
                        <div><span className="font-semibold">Device:</span> {fmtDevice(meta)}</div>
                        <div className="truncate"><span className="font-semibold">Browser:</span> {fmtBrowser(meta)}</div>
                        {g && (
                          <>
                            <div><span className="font-semibold">First seen:</span> {format(new Date(g.firstSeen), "dd MMM HH:mm")}</div>
                            <div><span className="font-semibold">Last seen:</span> {format(new Date(g.lastSeen), "dd MMM HH:mm")}</div>
                          </>
                        )}
                      </div>

                      {isScriptError(r) && (
                        <div className="space-y-1.5 rounded-md border border-amber-300 bg-amber-50 px-2 py-1.5 text-[11px] text-amber-900">
                          <div>
                            Browser masked this as a generic "Script error" (cross-origin script or missing source map). Enable debug logging and reproduce on the affected page to capture the true stack.
                          </div>
                          <Button size="sm" variant="outline" className="h-7" onClick={() => enableDebugAndReproduce(r)}>
                            <Bug className="mr-1 h-3.5 w-3.5" /> Enable Debug Logging & Reproduce
                          </Button>
                        </div>
                      )}

                      <div className="flex flex-wrap gap-2 pt-1">
                        <Button size="sm" variant="outline" onClick={() => setDetailsRow(r)}>
                          <Eye className="mr-1 h-3.5 w-3.5" /> View Full Details
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => copyToClipboard(buildLovableFixPrompt(r), "Lovable fix prompt").then(() => updateStatus.mutate({ ids: [r.id], status: "fix_prompt_copied" }))}>
                          <Wand2 className="mr-1 h-3.5 w-3.5" /> Copy Lovable Fix Prompt
                        </Button>
                        {st !== "fixed" ? (
                          <Button size="sm" variant="outline" onClick={() => updateStatus.mutate({ ids: g ? g.rows.map((x) => x.id) : [r.id], status: "fixed" })}>
                            <CheckCircle2 className="mr-1 h-3.5 w-3.5" /> Mark as Fixed
                          </Button>
                        ) : (
                          <Button size="sm" variant="outline" onClick={() => updateStatus.mutate({ ids: g ? g.rows.map((x) => x.id) : [r.id], status: "new" })}>
                            <RotateCcw className="mr-1 h-3.5 w-3.5" /> Reopen
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      <FullDetailsDialog
        row={detailsRow}
        onClose={() => setDetailsRow(null)}
        onStatus={(s, note) => detailsRow && updateStatus.mutate({ ids: [detailsRow.id], status: s, note })}
        onEnableDebug={() => enableDebugAndReproduce(detailsRow)}
      />
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1.5">
      <div className="text-[11px] font-bold uppercase tracking-wide text-muted-foreground">{title}</div>
      <div className="rounded border border-border bg-muted/40 p-2 text-xs">{children}</div>
    </div>
  );
}

function KV({ k, v }: { k: string; v: any }) {
  return (
    <div className="flex flex-wrap gap-1.5">
      <span className="font-semibold">{k}:</span>
      <span className="break-all">{nn(v)}</span>
    </div>
  );
}

function FullDetailsDialog({
  row,
  onClose,
  onStatus,
  onEnableDebug,
}: {
  row: Row | null;
  onClose: () => void;
  onStatus: (s: string, note?: string | null) => void;
  onEnableDebug: () => void;
}) {
  const [note, setNote] = useState<string>("");
  const meta = row?.meta ?? {};
  const prompt = row ? buildLovableFixPrompt(row) : "";
  const report = row ? buildFullReport(row) : "";
  const fetches = extractFetchFailures(meta);
  const cons = extractConsoleLogs(meta);
  const actions = extractRecentActions(meta);

  return (
    <Dialog open={!!row} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="flex max-h-[92vh] w-[95vw] max-w-4xl flex-col overflow-hidden p-0">
        <DialogHeader className="border-b px-4 py-3">
          <DialogTitle className="text-base">Full error report</DialogTitle>
          <DialogDescription className="text-xs">
            {row ? format(new Date(row.created_at), "dd MMM yyyy, HH:mm:ss") : ""}
          </DialogDescription>
        </DialogHeader>

        {row && (
          <div className="flex-1 space-y-4 overflow-y-auto px-4 py-3 text-xs">
            <Section title="Error Summary">
              <div className="grid grid-cols-1 gap-1 sm:grid-cols-2">
                <KV k="Message" v={row.message} />
                <KV k="Type" v={meta.name} />
                <KV k="Category" v={row.category} />
                <KV k="Severity" v={row.severity} />
                <KV k="Status" v={STATUS_LABEL[row.status ?? "new"] ?? row.status} />
                <KV k="Time" v={format(new Date(row.created_at), "dd MMM yyyy, HH:mm:ss")} />
              </div>
            </Section>

            <Section title="Where It Happened">
              <div className="grid grid-cols-1 gap-1 sm:grid-cols-2">
                <KV k="Page/Route" v={row.page} />
                <KV k="Full URL" v={meta.url} />
                <KV k="Source File" v={meta.filename ?? whyMissing(meta)} />
                <KV k="Line" v={meta.lineno ?? whyMissing(meta)} />
                <KV k="Column" v={meta.colno ?? whyMissing(meta)} />
                <KV k="Function/Component" v={meta.function_name ?? meta.boundary} />
                <KV
                  k="Source Maps"
                  v={
                    meta.source_maps_available === true
                      ? `Available (${meta.source_maps_reason ?? "ok"})`
                      : `Unavailable (${meta.source_maps_reason ?? "unknown"})`
                  }
                />
                {meta.raw_filename && meta.raw_filename !== meta.filename ? (
                  <>
                    <KV k="Minified File" v={meta.raw_filename} />
                    <KV k="Minified Line:Col" v={`${meta.raw_lineno ?? "?"}:${meta.raw_colno ?? "?"}`} />
                  </>
                ) : null}
              </div>
              {meta.script_error_explanation ? (
                <div className="mt-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900">
                  <div className="mb-1 font-semibold">Why file/line/column are missing</div>
                  {meta.script_error_explanation}
                </div>
              ) : null}
            </Section>

            <Section title="Device & App Information">
              <div className="grid grid-cols-1 gap-1 sm:grid-cols-2">
                <KV k="App Version" v={meta.app_version} />
                <KV k="Device Model" v={meta.device_model} />
                <KV k="OS" v={`${meta.os ?? ""} ${meta.os_version ?? ""}`.trim()} />
                <KV k="Android Version" v={meta.android_version} />
                <KV k="Browser/WebView" v={fmtBrowser(meta)} />
                <KV k="Viewport" v={fmtViewport(meta.viewport)} />
                <KV k="Network Online" v={meta.network_online} />
                <KV k="Connection" v={meta.connection ? JSON.stringify(meta.connection) : null} />
                <KV k="User ID" v={row.user_id} />
                <KV k="User Mobile" v={row.user_phone} />
                <KV k="User Agent" v={meta.user_agent} />
              </div>
            </Section>

            <Section title="Stack Trace">
              <pre className="max-h-64 overflow-auto whitespace-pre-wrap break-all font-mono text-[11px]">{row.stack ?? NA}</pre>
              <div className="mt-2">
                <Button size="sm" variant="outline" onClick={() => copyToClipboard(row.stack ?? "", "Stack trace")}>
                  <Copy className="mr-1 h-3.5 w-3.5" /> Copy Stack Trace
                </Button>
              </div>
            </Section>

            <Section title="Console Logs (recent)">
              {cons.length === 0 ? (
                <div className="italic text-muted-foreground">{NA}</div>
              ) : (
                <pre className="max-h-48 overflow-auto whitespace-pre-wrap font-mono text-[11px]">{cons.map(fmtBreadcrumbLine).join("\n")}</pre>
              )}
              <div className="mt-2">
                <Button size="sm" variant="outline" onClick={() => copyToClipboard(cons.map(fmtBreadcrumbLine).join("\n"), "Console logs")}>
                  <Copy className="mr-1 h-3.5 w-3.5" /> Copy Console Logs
                </Button>
              </div>
            </Section>

            <Section title="Network / API Failures">
              {fetches.length === 0 ? (
                <div className="italic text-muted-foreground">{NA}</div>
              ) : (
                <ul className="space-y-1">
                  {fetches.map((b, i) => {
                    const d = b.data ?? {};
                    return (
                      <li key={i} className="break-all">
                        <span className="font-mono">{d.method ?? "?"}</span> {d.url ?? b.message} →{" "}
                        <span className="font-semibold">{d.status ?? "network error"}</span>
                        {d.ms ? ` (${d.ms}ms)` : ""}
                      </li>
                    );
                  })}
                </ul>
              )}
              <div className="mt-2">
                <Button size="sm" variant="outline" onClick={() => copyToClipboard(JSON.stringify(fetches, null, 2), "API failure details")}>
                  <Copy className="mr-1 h-3.5 w-3.5" /> Copy API Failure Details
                </Button>
              </div>
            </Section>

            <Section title="Backend / Supabase Details">
              <KV k="Source" v={row.source} />
              <KV k="Category" v={row.category} />
              <div className="mt-1 text-muted-foreground italic">Backend errors from server functions surface here when the client logs them; check Edge Function logs for full server traces.</div>
            </Section>

            <Section title="Recent User Actions">
              {actions.length === 0 ? (
                <div className="italic text-muted-foreground">{NA}</div>
              ) : (
                <pre className="max-h-48 overflow-auto whitespace-pre-wrap font-mono text-[11px]">{actions.map(fmtBreadcrumbLine).join("\n")}</pre>
              )}
            </Section>

            <Section title="Suggested Cause">
              <div>{suggestedCause(row)}</div>
            </Section>

            <Section title="Ready-to-Copy Lovable Fix Prompt">
              <Textarea readOnly value={prompt} className="min-h-56 font-mono text-[11px]" />
              <div className="mt-2 flex flex-wrap gap-2">
                <Button size="sm" onClick={() => { copyToClipboard(prompt, "Lovable fix prompt"); onStatus("fix_prompt_copied"); }}>
                  <Wand2 className="mr-1 h-3.5 w-3.5" /> Copy Lovable Fix Prompt
                </Button>
                <Button size="sm" variant="outline" onClick={() => copyToClipboard(report, "Full error report")}>
                  <Copy className="mr-1 h-3.5 w-3.5" /> Copy Full Error Report
                </Button>
              </div>
            </Section>

            <Section title="Admin Note & Workflow">
              <Textarea
                placeholder="Add an internal note about this error (visible to admins only)"
                defaultValue={row.admin_note ?? ""}
                onChange={(e) => setNote(e.target.value)}
                className="min-h-20 text-xs"
              />
              <div className="mt-2 flex flex-wrap gap-2">
                <Button size="sm" variant="outline" onClick={() => onStatus(row.status ?? "new", note)}>
                  <StickyNote className="mr-1 h-3.5 w-3.5" /> Save Note
                </Button>
                <Button size="sm" variant="outline" onClick={() => onStatus("investigating")}>Mark Investigating</Button>
                <Button size="sm" variant="outline" onClick={() => onStatus("fixed")}>
                  <CheckCircle2 className="mr-1 h-3.5 w-3.5" /> Mark as Fixed
                </Button>
                <Button size="sm" variant="outline" onClick={() => onStatus("ignored")}>Ignore</Button>
                <Button size="sm" variant="outline" onClick={() => onStatus("new")}>
                  <RotateCcw className="mr-1 h-3.5 w-3.5" /> Reopen
                </Button>
                <Button size="sm" variant="outline" onClick={onEnableDebug}>
                  <Bug className="mr-1 h-3.5 w-3.5" /> Enable Debug & Reproduce
                </Button>
              </div>
            </Section>

            {row.screenshot_path && <ScreenshotLink path={row.screenshot_path} />}

            <details className="rounded border border-border bg-muted/30 p-2">
              <summary className="cursor-pointer text-[11px] font-semibold uppercase text-muted-foreground">Raw meta (masked)</summary>
              <pre className="mt-2 max-h-64 overflow-auto rounded bg-background p-2 font-mono text-[11px]">
                {JSON.stringify(maskObject(meta), null, 2)}
              </pre>
            </details>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function ScreenshotLink({ path }: { path: string }) {
  const [url, setUrl] = useState<string | null>(null);
  async function open() {
    const { data } = await supabase.storage.from("error-screenshots").createSignedUrl(path, 300);
    if (data?.signedUrl) { setUrl(data.signedUrl); window.open(data.signedUrl, "_blank"); }
  }
  return (
    <button onClick={open} className="text-xs font-semibold text-primary underline">
      {url ? "Reopen screenshot" : "View screenshot"}
    </button>
  );
}
